import { useState } from 'react';
import type { ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { BotApi } from '../adapters/BotApi';
import type { UserProfile } from '../domain/types';
import { EmptyState, ErrorState, Panel } from './components';
import { useI18n } from '../i18n';

function GoogleLogo() {
  return (
    <svg className="google-logo" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l3.66-2.84z"
      />
      <path
        fill="#EA4335"
        d="M12 5.37c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06L5.84 9.9C6.71 7.3 9.14 5.37 12 5.37z"
      />
    </svg>
  );
}

export function AuthGate({
  api,
  children
}: {
  api: BotApi;
  children: (user: UserProfile | null, signOut: () => Promise<void>) => ReactNode;
}) {
  const queryClient = useQueryClient();
  const { t } = useI18n();
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');

  const sessionQuery = useQuery({
    queryKey: ['session'],
    queryFn: async () => (api.getSession ? api.getSession() : api.getCurrentUser()),
    retry: false
  });

  const emailMutation = useMutation({
    mutationFn: async () => {
      if (!api.signInWithEmail) return;
      await api.signInWithEmail(email);
    },
    onSuccess: () => setMessage(t('auth.checkEmail')),
    onError: (error) => setMessage(error instanceof Error ? error.message : String(error))
  });

  const googleMutation = useMutation({
    mutationFn: async () => {
      if (!api.signInWithGoogle) return;
      setMessage(t('auth.googleOpening'));
      await api.signInWithGoogle();
    },
    onError: (error) => setMessage(error instanceof Error ? error.message : String(error))
  });

  if (sessionQuery.isLoading) {
    return (
      <main className="auth-shell">
        <Panel title="White Bot">
          <EmptyState>{t('auth.loadingSession')}</EmptyState>
        </Panel>
      </main>
    );
  }

  if (api.mode !== 'supabase') {
    const signOut = async () => {
      localStorage.clear();
      sessionStorage.clear();
      await queryClient.invalidateQueries({ queryKey: ['session'] });
      window.location.href = '/';
    };
    return <>{children(sessionQuery.data || null, signOut)}</>;
  }

  if (sessionQuery.data) {
    const signOut = async () => {
      await api.signOut?.();
      localStorage.clear();
      sessionStorage.clear();
      await queryClient.invalidateQueries({ queryKey: ['session'] });
      window.location.href = '/';
    };
    return <>{children(sessionQuery.data, signOut)}</>;
  }

  return (
    <main className="auth-shell">
      <Panel title="White Bot">
        {sessionQuery.error && <ErrorState error={sessionQuery.error} />}
        <p className="muted">{t('auth.subtitle')}</p>
        <label>
          {t('auth.email')}
          <input value={email} onChange={(event) => setEmail(event.target.value)} placeholder={t('auth.placeholderEmail')} />
        </label>
        <button className="primary" disabled={!email || emailMutation.isPending} onClick={() => emailMutation.mutate()}>
          {t('auth.sendLink')}
        </button>
        <button
          className="google-login-button"
          disabled={googleMutation.isPending}
          onClick={() => googleMutation.mutate()}
        >
          <GoogleLogo />
          <span>{googleMutation.isPending ? t('auth.googleOpening') : t('auth.googleLogin')}</span>
        </button>
        <div className="muted">{message}</div>
      </Panel>
    </main>
  );
}
